import { getErrorMessage } from '@/lib/functions';
import { useTranslation } from 'react-i18next';
import { Ionicons } from '@expo/vector-icons';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  ActivityIndicator,
  FlatList,
  Image,
  Linking,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import * as Clipboard from 'expo-clipboard';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Avatar } from '@/components/ui/Avatar';
import { ChatBubble } from '@/components/ChatBubble';
import { DisputeOutcomeCard } from '@/components/DisputeOutcomeCard';
import { LevelBadge, type LevelInfo } from '@/components/ui/LevelBadge';
import { VerifiedBadge } from '@/components/ui/VerifiedBadge';
import { useTheme } from '@/contexts/ThemeContext';
import { fontSize, radius, shadow, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import type { Profile } from '@/lib/database.types';
import { fetchDisputesForProfile, type DisputeOutcomeEntry } from '@/lib/disputes';
import { formatDate } from '@/lib/format';
import { getUserLevel } from '@/lib/levels';
import { getPortfolio, type Portfolio, type PortfolioAgreement } from '@/lib/portfolio';
import { tagLabel, characterizationLabel } from '@/lib/reviews';
import type { ReviewTag } from '@/lib/database.types';
import { fetchProfile } from '@/lib/profiles';
import { effectiveAvatarUrl } from '@/lib/avatar';
import { fetchServicesForProfile, type ProfessionalService } from '@/lib/professionalServices';
import { ErrorText } from '@/components/ErrorText';

export default function PortfolioScreen() {
  const { t } = useTranslation();
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { user } = useAuth();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const isOwnPortfolio = !!user && user.id === id;

  const [profile, setProfile] = useState<Profile | null>(null);
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [levelInfo, setLevelInfo] = useState<LevelInfo | null>(null);
  const [services, setServices] = useState<ProfessionalService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPayments, setShowPayments] = useState(false);
  const [showBusinessMembers, setShowBusinessMembers] = useState(false);
  const [disputesByAgreement, setDisputesByAgreement] = useState<Record<string, DisputeOutcomeEntry>>({});

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const [p, pf, lvl, svcs] = await Promise.all([
        fetchProfile(id),
        getPortfolio(id, { preview: isOwnPortfolio }).catch(() => null),
        getUserLevel(id).catch(() => null),
        fetchServicesForProfile(id).catch(() => []),
      ]);
      setProfile(p);
      setPortfolio(pf);
      setLevelInfo(lvl);
      setServices(svcs);

      // Ίδια πλούσια απεικόνιση διαφωνίας (ποιος έφταιξε) με το προσωπικό
      // προφίλ — μαζεύουμε disputes για όλα τα μέλη της επιχείρησης, ώστε
      // να καλύπτονται και οι συμφωνίες που δημιούργησε συνέταιρος.
      const memberIds = [...new Set([id, ...(pf?.business_members?.map((m) => m.id) ?? [])])];
      const disputeLists = await Promise.all(memberIds.map((mid) => fetchDisputesForProfile(mid).catch(() => [])));
      const map: Record<string, DisputeOutcomeEntry> = {};
      for (const list of disputeLists) for (const entry of list) map[entry.agreement_id] = entry;
      setDisputesByAgreement(map);
    } catch (e) {
      setError(getErrorMessage(e, t('errors.generic')));
    } finally {
      setLoading(false);
    }
  }, [id, isOwnPortfolio]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <View style={styles.loader}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (!portfolio) {
    return (
      <View style={styles.loader}>
        <Ionicons name="briefcase-outline" size={48} color={colors.textMuted} />
        <Text style={styles.emptyText}>{t('portfolio.noAccount')}</Text>
        <Pressable onPress={() => router.back()} style={styles.backBtn}>
          <Text style={styles.backBtnText}>{t('common.back')}</Text>
        </Pressable>
      </View>
    );
  }

  const isPreview = isOwnPortfolio && !portfolio.enabled;
  const businessMemberIds = [...new Set([id, ...(portfolio.business_members?.map((m) => m.id) ?? [])])];

  const personalName = profile?.full_name?.trim() || t('common.user');
  const useBusinessName = !!profile?.show_business_name && !!profile?.business_name?.trim();
  const name = useBusinessName ? profile!.business_name!.trim() : personalName;

  return (
    <View style={styles.flex}>
    {!isOwnPortfolio && <ChatBubble otherUserId={id} label={name} />}
    <FlatList
      style={styles.flex}
      contentContainerStyle={[
        styles.content,
        { paddingTop: insets.top + spacing.lg, paddingBottom: insets.bottom + spacing.xl },
      ]}
      ListHeaderComponent={
        <>
          <View style={styles.topBar}>
            <Pressable onPress={() => router.back()} hitSlop={12} accessibilityRole="button">
              <Text style={styles.back}>{t('common.back')}</Text>
            </Pressable>
            <Text style={styles.screenTitle}>{t('user.professionalProfile')}</Text>
            <View style={styles.spacer} />
          </View>

          {isPreview && (
            <View style={styles.previewBanner}>
              <Ionicons name="eye-outline" size={16} color="#92400E" />
              <Text style={styles.previewBannerText}>
                {t('user.portfolioPreviewBanner')}
              </Text>
            </View>
          )}

          {/* Header χρήστη */}
          <View style={styles.header}>
            <Avatar
              uri={useBusinessName && profile?.business_logo_url ? profile.business_logo_url : effectiveAvatarUrl(profile)}
              name={name}
              size={useBusinessName ? 104 : 80}
            />
            <Text
              style={[styles.name, useBusinessName && styles.businessNameLarge]}
              numberOfLines={2}
              adjustsFontSizeToFit
              minimumFontScale={0.7}
            >
              {name}
            </Text>
            {useBusinessName && (
              <View style={styles.responsibleRow}>
                <Avatar uri={effectiveAvatarUrl(profile)} name={personalName} size={22} />
                <Text style={styles.responsibleText} numberOfLines={1}>
                  {t('user.responsiblePerson', { name: personalName })}
                </Text>
                {portfolio.total_count > 0 && (
                  <View style={styles.responsibleReliability}>
                    <Ionicons name="checkmark-circle" size={12} color={colors.success} />
                    <Text style={styles.responsibleReliabilityText}>{portfolio.completion_rate}%</Text>
                  </View>
                )}
              </View>
            )}
            {(!!profile?.verified || !!levelInfo) && (
              <View style={styles.nameRow}>
                {!!profile?.verified && <VerifiedBadge compact />}
                {levelInfo && (
                  <LevelBadge
                    info={levelInfo}
                    size="md"
                    memberSince={profile?.created_at ? formatDate(profile.created_at) : undefined}
                    avgRating={portfolio.avg_rating}
                  />
                )}
              </View>
            )}
            {!!profile?.business_logo_url && !useBusinessName && (
              <Image
                source={{ uri: profile.business_logo_url }}
                style={styles.businessLogo}
                resizeMode="contain"
              />
            )}

            {(() => {
              // Η διεύθυνση/eshop είναι κοινά για όλη την επιχείρηση — αν ο
              // συγκεκριμένος συνέταιρος δεν τα έχει συμπληρώσει ο ίδιος,
              // παίρνουμε αυτά που επέστρεψε το get_portfolio από οποιονδήποτε
              // άλλο συνέταιρο. Ποτέ δεν χρησιμοποιείται προσωπική διεύθυνση —
              // μόνο business_address υπάρχει ως πεδίο στη βάση.
              const sharedEshopUrl = portfolio?.eshop_url ?? (profile as any)?.eshop_url ?? null;
              const sharedAddress = portfolio?.business_address ?? profile?.business_address ?? null;
              const sharedCity = portfolio?.business_city ?? profile?.business_city ?? null;
              const sharedPostal = portfolio?.business_postal_code ?? profile?.business_postal_code ?? null;
              const hiddenMemberCount = portfolio.hidden_member_count ?? 0;
              const hasBusinessMembers = !!portfolio.business_id
                && ((portfolio.business_members?.length ?? 0) > 1 || hiddenMemberCount > 0);
              const hasPaymentMethods = ((profile as any)?.payment_ibans?.length ?? 0) > 0
                || !!(profile as any)?.payment_iris || !!(profile as any)?.payment_revolut_link || !!(profile as any)?.payment_viva_link;

              // Όλα τα δευτερεύοντα "κουμπιά" μαζεμένα σε μία σειρά αντί για
              // διάσπαρτα σημεία στην οθόνη — ό,τι δεν είναι βασικό (αξιολόγηση,
              // υπηρεσίες) μένει εδώ, σαν μικρά chips.
              return (
                <>
                  <View style={styles.metaChipRow}>
                    {!!portfolio.specialty && (
                      <View style={styles.specialtyBadge}>
                        <Ionicons name="briefcase-outline" size={14} color={colors.primary} />
                        <Text style={styles.specialtyText} numberOfLines={1}>{portfolio.specialty}</Text>
                      </View>
                    )}
                    {!!sharedEshopUrl && (
                      <Pressable
                        style={styles.retailBtn}
                        onPress={() => { void Linking.openURL(sharedEshopUrl); }}
                        accessibilityRole="button"
                      >
                        <Ionicons name="storefront-outline" size={16} color={colors.primary} />
                        <Text style={styles.retailBtnText}>{t('user.visitEshop')}</Text>
                      </Pressable>
                    )}
                    {!!sharedAddress && (
                      <Pressable
                        style={styles.retailBtn}
                        onPress={() => {
                          const q = encodeURIComponent(
                            [sharedAddress, sharedCity, sharedPostal].filter(Boolean).join(', '),
                          );
                          void Linking.openURL(`https://www.google.com/maps/search/?api=1&query=${q}`);
                        }}
                        accessibilityRole="button"
                      >
                        <Ionicons name="location-outline" size={16} color={colors.primary} />
                        <Text style={styles.retailBtnText}>{t('user.openInMaps')}</Text>
                      </Pressable>
                    )}
                    {hasBusinessMembers && (
                      <Pressable
                        style={styles.secondaryToggleChip}
                        onPress={() => setShowBusinessMembers((v) => !v)}
                        accessibilityRole="button"
                      >
                        <Ionicons name="people-outline" size={14} color={colors.textMuted} />
                        <Text style={styles.secondaryToggleChipText}>
                          {t('portfolio.businessMembers')} ({(portfolio.business_members?.length ?? 0) + hiddenMemberCount})
                        </Text>
                        <Ionicons name={showBusinessMembers ? 'chevron-up' : 'chevron-down'} size={14} color={colors.textMuted} />
                      </Pressable>
                    )}
                    {hasPaymentMethods && (
                      <Pressable
                        style={styles.secondaryToggleChip}
                        onPress={() => setShowPayments((v) => !v)}
                        accessibilityRole="button"
                      >
                        <Ionicons name="card-outline" size={14} color={colors.textMuted} />
                        <Text style={styles.secondaryToggleChipText}>{t('profile.section_paymentMethods')}</Text>
                        <Ionicons name={showPayments ? 'chevron-up' : 'chevron-down'} size={14} color={colors.textMuted} />
                      </Pressable>
                    )}
                  </View>

                  {hasBusinessMembers && showBusinessMembers && (
                    <View style={styles.secondaryExpanded}>
                      {(portfolio.business_members ?? []).map((m) => (
                        <Pressable
                          key={m.id}
                          style={styles.businessMemberRow}
                          onPress={() => { if (m.id !== id) router.push({ pathname: '/(app)/users/portfolio/[id]', params: { id: m.id } }); }}
                          accessibilityRole="button"
                        >
                          <Avatar uri={m.avatar_url} name={m.full_name} size={28} />
                          <Text style={styles.businessMemberName} numberOfLines={1}>
                            {m.full_name || t('common.user')}{m.id === id ? ` (${t('portfolio.businessMemberViewing')})` : ''}
                          </Text>
                          {m.id !== id && <Ionicons name="chevron-forward" size={16} color={colors.textMuted} />}
                        </Pressable>
                      ))}
                      {hiddenMemberCount > 0 && (
                        <View style={styles.businessMemberRow}>
                          <View style={styles.businessMemberHiddenAvatar}>
                            <Ionicons name="person" size={14} color={colors.textMuted} />
                          </View>
                          <Text style={styles.businessMemberName} numberOfLines={1}>
                            {t('portfolio.businessHiddenMembers', { count: hiddenMemberCount })}
                          </Text>
                        </View>
                      )}
                    </View>
                  )}

                  {hasPaymentMethods && showPayments && (
                    <View style={styles.secondaryExpanded}>
                      <Text style={styles.paymentDisclaimer}>{t('portfolio.paymentMethodsDisclaimer')}</Text>
                      {((profile as any)?.payment_ibans ?? []).map((entry: { bank: string | null; iban: string }, idx: number) => (
                        <Pressable
                          key={idx}
                          style={styles.paymentRow}
                          onPress={() => { void Clipboard.setStringAsync(entry.iban); }}
                          accessibilityRole="button"
                        >
                          <Ionicons name="card-outline" size={16} color={colors.primary} />
                          <Text style={styles.paymentLabel} numberOfLines={1}>{entry.bank?.trim() || 'IBAN'}</Text>
                          <Text style={styles.paymentValue} numberOfLines={1}>{entry.iban}</Text>
                          <Ionicons name="copy-outline" size={14} color={colors.textMuted} />
                        </Pressable>
                      ))}
                      {!!(profile as any)?.payment_iris && (
                        <Pressable
                          style={styles.paymentRow}
                          onPress={() => { void Clipboard.setStringAsync((profile as any).payment_iris); }}
                          accessibilityRole="button"
                        >
                          <Ionicons name="flash-outline" size={16} color={colors.primary} />
                          <Text style={styles.paymentLabel}>IRIS</Text>
                          <Text style={styles.paymentValue} numberOfLines={1}>{(profile as any).payment_iris}</Text>
                          <Ionicons name="copy-outline" size={14} color={colors.textMuted} />
                        </Pressable>
                      )}
                      {!!(profile as any)?.payment_revolut_link && (
                        <Pressable
                          style={styles.paymentRow}
                          onPress={() => { void Linking.openURL((profile as any).payment_revolut_link); }}
                          accessibilityRole="button"
                        >
                          <Ionicons name="link-outline" size={16} color={colors.primary} />
                          <Text style={styles.paymentLabel}>Revolut</Text>
                          <Text style={styles.paymentValue} numberOfLines={1}>{t('portfolio.openLink')}</Text>
                          <Ionicons name="open-outline" size={14} color={colors.textMuted} />
                        </Pressable>
                      )}
                      {!!(profile as any)?.payment_viva_link && (
                        <Pressable
                          style={styles.paymentRow}
                          onPress={() => { void Linking.openURL((profile as any).payment_viva_link); }}
                          accessibilityRole="button"
                        >
                          <Ionicons name="link-outline" size={16} color={colors.primary} />
                          <Text style={styles.paymentLabel}>Viva Wallet</Text>
                          <Text style={styles.paymentValue} numberOfLines={1}>{t('portfolio.openLink')}</Text>
                          <Ionicons name="open-outline" size={14} color={colors.textMuted} />
                        </Pressable>
                      )}
                    </View>
                  )}
                </>
              );
            })()}

            {/* Info strip */}
            <View style={styles.infoStrip}>
              {!!profile?.created_at && (
                <View style={styles.infoItem}>
                  <Ionicons name="calendar-outline" size={12} color={colors.textMuted} />
                  <Text style={styles.infoText}>{t('user.memberSince')} {formatDate(profile.created_at)}</Text>
                </View>
              )}
              {!!profile?.phone_verified && (
                <View style={styles.infoItem}>
                  <Ionicons name="phone-portrait-outline" size={12} color={colors.success} />
                  <Text style={[styles.infoText, { color: colors.success }]}>{t('profile.phone_verified')}</Text>
                </View>
              )}
              {!!profile?.verified && (
                <View style={styles.infoItem}>
                  <Ionicons name="shield-checkmark-outline" size={12} color={colors.success} />
                  <Text style={[styles.infoText, { color: colors.success }]}>{t('user.verifiedIdentity')}</Text>
                </View>
              )}
            </View>
          </View>

          {/* Stats row */}
          <View style={styles.statsRow}>
            <StatBox label={t('portfolio.stat_completed')} value={String(portfolio.completed_count)} styles={styles} />
            <StatBox label={t('portfolio.stat_success')} value={`${portfolio.completion_rate}%`} styles={styles} />
            {portfolio.avg_rating != null && (
              <StatBox label={t('portfolio.stat_rating')} value={portfolio.avg_rating.toFixed(1) + ' ★'} styles={styles} />
            )}
          </View>

          {(() => {
            const characterization = characterizationLabel(
              portfolio.total_count > 0 ? portfolio.completion_rate : null,
              portfolio.avg_rating ?? 0,
              portfolio.avg_rating != null,
              portfolio.completed_count,
            );
            return characterization ? (
              <View style={styles.characterizationBadge}>
                <Text style={styles.characterizationText}>{characterization}</Text>
              </View>
            ) : null;
          })()}

          {!isOwnPortfolio && (
            <Pressable
              style={styles.requestQuoteBtn}
              onPress={() => router.push({
                pathname: '/(app)/requests/new',
                params: {
                  targetProfessionalId: id,
                  targetProfessionalName: profile?.full_name ?? '',
                },
              } as any)}
              accessibilityRole="button"
            >
              <Ionicons name="chatbox-ellipses-outline" size={18} color={colors.textOnPrimary} />
              <Text style={styles.requestQuoteBtnText}>{t('user.requestQuoteGeneral')}</Text>
            </Pressable>
          )}

          {services.length > 0 && (
            <View style={styles.paymentCard}>
              <Text style={styles.sectionTitle}>{t('services.sectionTitle')}</Text>
              {services.map((s) => (
                <View key={s.id} style={styles.serviceRow}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.serviceTitle}>{s.title}</Text>
                    {!!s.description && <Text style={styles.serviceDesc} numberOfLines={2}>{s.description}</Text>}
                    {s.price != null && (
                      <Text style={styles.servicePrice}>
                        {s.price.toFixed(2)} € {s.price_unit !== 'fixed' ? t(`services.unit_${s.price_unit}`) : ''}
                      </Text>
                    )}
                  </View>
                  {!isOwnPortfolio && (
                    <Pressable
                      style={styles.quoteBtn}
                      onPress={() => router.push({
                        pathname: '/(app)/requests/new',
                        params: {
                          targetProfessionalId: id,
                          targetProfessionalName: profile?.full_name ?? '',
                          presetTitle: s.title,
                        },
                      } as any)}
                      accessibilityRole="button"
                    >
                      <Text style={styles.quoteBtnText}>{t('services.requestQuote')}</Text>
                    </Pressable>
                  )}
                </View>
              ))}
            </View>
          )}

          {portfolio.agreements.length > 0 && (
            <Text style={styles.sectionTitle}>{t('portfolio.completedAgreements')}</Text>
          )}

          <ErrorText>{error}</ErrorText>
        </>
      }
      data={portfolio.agreements}
      keyExtractor={(item) => item.id}
      renderItem={({ item }) => (
        <AgreementCard
          item={item}
          disputeEntry={disputesByAgreement[item.id]}
          businessMemberIds={businessMemberIds}
          styles={styles}
          colors={colors}
        />
      )}
      ListEmptyComponent={
        <Text style={styles.emptyText}>{t('portfolio.noCompletedAgreements')}</Text>
      }
      ItemSeparatorComponent={() => <View style={{ height: spacing.sm }} />}
    />
    </View>
  );
}

function StatBox({ label, value, styles }: { label: string; value: string; styles: ReturnType<typeof makeStyles> }) {
  return (
    <View style={styles.statBox}>
      <Text style={styles.statValue}>{value}</Text>
      <Text style={styles.statLabel}>{label}</Text>
    </View>
  );
}

function AgreementCard({ item, disputeEntry, businessMemberIds, styles, colors }: {
  item: PortfolioAgreement;
  disputeEntry?: DisputeOutcomeEntry;
  businessMemberIds: string[];
  styles: ReturnType<typeof makeStyles>;
  colors: AppColors;
}) {
  const { t } = useTranslation();
  const isDisputed = item.status !== 'completed';
  return (
    <View style={styles.agreementCard}>
      {isDisputed && !disputeEntry && (
        <View style={styles.agreementDisputedBadge}>
          <Ionicons name="alert-circle-outline" size={12} color={colors.danger} />
          <Text style={styles.agreementDisputedText}>{t('portfolio.resolvedDispute')}</Text>
        </View>
      )}
      {isDisputed && !!disputeEntry && (
        <View style={{ marginBottom: spacing.xs }}>
          <DisputeOutcomeCard entry={disputeEntry} businessMemberIds={businessMemberIds} />
        </View>
      )}
      <View style={styles.agreementMain}>
        <Ionicons
          name={isDisputed ? 'alert-circle' : 'checkmark-circle'}
          size={18}
          color={isDisputed ? colors.danger : colors.success}
        />
        <Text style={styles.agreementTitle} numberOfLines={2}>{item.title}</Text>
      </View>
      <View style={styles.agreementMeta}>
        <Text style={styles.agreementDate}>{formatDate(item.completed_at)}</Text>
        {item.rating != null && (
          <Text style={styles.agreementRating}>{'★'.repeat(item.rating)}{'☆'.repeat(5 - item.rating)}</Text>
        )}
      </View>
      {!!item.tags?.length && (
        <View style={styles.tagsRow}>
          {item.tags.map((tag) => (
            <View key={tag} style={styles.tagPill}>
              <Text style={styles.tagPillText}>{tagLabel(tag as ReviewTag)}</Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: colors.background },
    loader: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.background,
      gap: spacing.md,
    },
    content: { paddingHorizontal: spacing.lg, gap: spacing.lg },
    topBar: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    back: { color: colors.primaryLight, fontSize: fontSize.md, fontWeight: '600', width: 60 },
    spacer: { width: 60 },
    screenTitle: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700' },
    header: { alignItems: 'center', gap: spacing.xs, paddingHorizontal: spacing.sm },
    nameRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, flexWrap: 'wrap', justifyContent: 'center' },
    name: { color: colors.text, fontSize: fontSize.xl, fontWeight: '700', textAlign: 'center' },
    businessNameLarge: { fontSize: fontSize.xxl, fontWeight: '800' },
    responsibleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      marginTop: 2,
    },
    responsibleText: { color: colors.textMuted, fontSize: fontSize.sm },
    responsibleReliability: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 2,
      marginLeft: 2,
    },
    responsibleReliabilityText: { color: colors.success, fontSize: fontSize.sm, fontWeight: '600' },
    // Όλα τα δευτερεύοντα chips (ειδικότητα, eShop, χάρτης, συνέταιροι,
    // τρόποι πληρωμής) μαζεμένα σε μία σειρά αντί για διάσπαρτα σημεία.
    metaChipRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: spacing.xs,
      marginTop: spacing.sm,
    },
    specialtyBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.full,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.xs,
      maxWidth: '90%',
    },
    specialtyText: { color: colors.primary, fontSize: fontSize.sm, fontWeight: '600', flexShrink: 1 },
    businessLogo: {
      height: 40,
      width: 140,
      borderRadius: radius.sm,
      borderWidth: 1,
      borderColor: colors.border,
      marginTop: spacing.xs,
    },
    statsRow: { flexDirection: 'row', gap: spacing.sm },
    characterizationBadge: {
      alignSelf: 'flex-start',
      backgroundColor: `${colors.primary}14`,
      borderRadius: radius.full,
      paddingVertical: 3,
      paddingHorizontal: spacing.sm,
      marginTop: spacing.xs,
    },
    characterizationText: {
      color: colors.primary,
      fontSize: 12,
      fontWeight: '700',
    },
    statBox: {
      flex: 1,
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.md,
      padding: spacing.md,
      alignItems: 'center',
      gap: 4,
    },
    statValue: { color: colors.text, fontSize: fontSize.lg, fontWeight: '800' },
    statLabel: { color: colors.textMuted, fontSize: 10, textAlign: 'center', lineHeight: 13 },
    infoStrip: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: spacing.sm,
      marginTop: 2,
    },
    infoItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    infoText: {
      color: colors.textMuted,
      fontSize: 11,
    },
    sectionTitle: { color: colors.text, fontSize: fontSize.lg, fontWeight: '600' },
    businessMemberRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.xs },
    businessMemberHiddenAvatar: {
      width: 28, height: 28, borderRadius: 14,
      backgroundColor: colors.surfaceAlt,
      alignItems: 'center', justifyContent: 'center',
    },
    businessMemberName: { flex: 1, color: colors.text, fontSize: fontSize.sm, fontWeight: '600' },
    retailBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.full,
      paddingVertical: 8,
      paddingHorizontal: spacing.md,
    },
    retailBtnText: {
      color: colors.primary,
      fontSize: fontSize.sm,
      fontWeight: '700',
    },
    requestQuoteBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.xs,
      backgroundColor: colors.primary,
      borderRadius: radius.md,
      paddingVertical: spacing.sm + 2,
      marginTop: spacing.md,
      marginBottom: spacing.sm,
    },
    requestQuoteBtnText: {
      color: colors.textOnPrimary,
      fontSize: fontSize.md,
      fontWeight: '700',
    },
    serviceRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      paddingVertical: 8,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    serviceTitle: { color: colors.text, fontSize: fontSize.sm, fontWeight: '700' },
    serviceDesc: { color: colors.textMuted, fontSize: fontSize.xs, marginTop: 2 },
    servicePrice: { color: colors.primary, fontSize: fontSize.sm, fontWeight: '700', marginTop: 2 },
    quoteBtn: {
      borderWidth: 1,
      borderColor: colors.primary,
      borderRadius: radius.full,
      paddingVertical: 6,
      paddingHorizontal: spacing.sm,
    },
    quoteBtnText: { color: colors.primary, fontSize: fontSize.xs, fontWeight: '700' },
    paymentCard: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
      gap: spacing.xs,
    },
    // Δευτερεύουσες πληροφορίες (συνέταιροι, τρόποι πληρωμής) — πιο διακριτικά
    // από τα βασικά (αξιολόγηση, υπηρεσίες), σαν μικρό κουμπί που ανοίγει.
    secondaryToggleChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.full,
      paddingVertical: 6,
      paddingHorizontal: spacing.sm,
    },
    secondaryToggleChipText: { color: colors.textMuted, fontSize: fontSize.xs, fontWeight: '600' },
    secondaryExpanded: {
      alignSelf: 'stretch',
      backgroundColor: colors.surface,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.sm,
      gap: spacing.xs,
    },
    paymentDisclaimer: {
      color: colors.textMuted,
      fontSize: fontSize.xs,
      lineHeight: 16,
      marginBottom: 4,
    },
    paymentToggleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
    paymentCollapsedHint: { color: colors.primary, fontSize: fontSize.xs, fontWeight: '600' },
    paymentRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      paddingVertical: 8,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    paymentLabel: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '700',
      width: 78,
    },
    paymentValue: {
      flex: 1,
      color: colors.text,
      fontSize: fontSize.sm,
    },
    agreementCard: {
      backgroundColor: colors.surface,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
      gap: spacing.xs,
      ...shadow.card,
    },
    agreementDisputedBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      alignSelf: 'flex-start',
      backgroundColor: `${colors.danger}18`,
      borderRadius: radius.full,
      paddingHorizontal: spacing.sm,
      paddingVertical: 2,
    },
    agreementDisputedText: { color: colors.danger, fontSize: 11, fontWeight: '700' },
    agreementMain: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm },
    agreementTitle: { flex: 1, color: colors.text, fontSize: fontSize.md, fontWeight: '600' },
    agreementMeta: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
    agreementDate: { color: colors.textMuted, fontSize: fontSize.sm },
    agreementRating: { color: colors.accent, fontSize: fontSize.sm, letterSpacing: 1 },
    tagsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: spacing.xs },
    tagPill: {
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.full,
      paddingVertical: 3,
      paddingHorizontal: 8,
    },
    tagPillText: { color: colors.text, fontSize: 11, fontWeight: '500' },
    emptyText: { color: colors.textMuted, fontSize: fontSize.md, textAlign: 'center' },
    backBtn: { paddingVertical: spacing.sm, paddingHorizontal: spacing.lg },
    backBtnText: { color: colors.primaryLight, fontSize: fontSize.md, fontWeight: '600' },
    error: { color: colors.danger, fontSize: fontSize.sm, textAlign: 'center' },
    previewBanner: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: spacing.sm,
      backgroundColor: '#FEF3C7',
      borderRadius: radius.md,
      padding: spacing.md,
      borderWidth: 1,
      borderColor: '#FCD34D',
    },
    previewBannerText: {
      flex: 1,
      color: '#92400E',
      fontSize: fontSize.sm,
      lineHeight: 19,
      fontWeight: '500',
    },
  });
}
